import React, { memo } from "react";

import {
  SKELETON_INPUT_HEIGHT,
  SKELETON_TEXT_HEIGHT,
  SkeletonList,
} from "@hl/base-components/lib/Skeleton";
import {
  Box,
  Center,
  Flex,
  SimpleGrid,
  Skeleton,
  Stack,
  useMantineTheme,
  Group,
  Table,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { CardView } from "../marketplace-management/Collected";

const UserCollectedListSkeleton = ({
  view,
  showHeader = true,
}: {
  view: CardView;
  showHeader?: boolean;
}) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`);

  if (view === CardView.LineItem) {
    return (
      <Table
        verticalSpacing="md"
        miw={1200}
        sx={{
          "thead tr th": {
            paddingLeft: 0,
            paddingTop: 8,
          },
          "tbody tr td": {
            paddingLeft: 0,
          },
          "tbody tr td:last-child": {
            paddingRight: 0,
          },
        }}
      >
        {showHeader && (
          <thead>
            <tr>
              <th>
                <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
              </th>
              <th>
                <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
              </th>
              {!isMobile && (
                <>
                  <th>
                    <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                  </th>
                  <th>
                    <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                  </th>
                  <th>
                    <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                  </th>
                  <th></th>
                </>
              )}
            </tr>
          </thead>
        )}
        <tbody>
          <tr>
            <td>
              <Group noWrap>
                <Box p={8}>
                  <Skeleton height={64} width={64} />
                </Box>
                <Stack spacing={4}>
                  <Skeleton height={SKELETON_TEXT_HEIGHT} width={50} />
                  <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                </Stack>
              </Group>
            </td>
            <td>
              <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
            </td>
            {!isMobile && (
              <>
                <td>
                  <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                </td>
                <td>
                  <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                </td>
                <td>
                  <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                </td>
                <td>
                  <Skeleton height={SKELETON_INPUT_HEIGHT} width={270} />
                </td>
              </>
            )}
          </tr>
        </tbody>
      </Table>
    );
  }

  return (
    <SimpleGrid
      cols={4}
      spacing={40}
      verticalSpacing={56}
      breakpoints={[
        { maxWidth: "lg", cols: 3 },
        { maxWidth: "md", cols: 2 },
        { maxWidth: "sm", cols: 1, verticalSpacing: 40 },
      ]}
    >
      <SkeletonList
        count={isMobile ? 1 : 4}
        skeleton={
          <Box pos="relative">
            <Center
              sx={(theme) => ({
                minHeight: 240,
                aspectRatio: "1",
                height: "auto",
                border: `0.5px solid ${theme.colors.divider[0]}`,
              })}
            >
              <Skeleton width="100%" height="100%" />
            </Center>
            <Flex
              mt={12}
              justify="space-between"
              align="center"
              columnGap={8}
              rowGap={4}
              wrap="nowrap"
            >
              <Stack spacing={6}>
                <Skeleton height={SKELETON_TEXT_HEIGHT} width={100} />
                <Skeleton height={16} width={50} />
              </Stack>
            </Flex>
          </Box>
        }
      />
    </SimpleGrid>
  );
};

export default memo(UserCollectedListSkeleton);
